import { Fragment, useCallback, useMemo, useRef } from 'react';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import HR from 'UIComponents/elements/HR';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { useGetHasScope, useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import ErrorBoundary from 'automation-ui-core/errors/ErrorBoundary';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EXPANDED } from 'automation-ui-canvas/constants/settings/SettingsParams';
import { H5 } from 'UIComponents/elements/headings';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import { atomicQueryUpdate } from 'automation-ui-routing/historyUtils';
import { useHistory } from 'react-router-dom';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SettingsSection = ({
  title,
  modules,
  flow,
  readOnly = false,
  onChange,
  settings,
  salesforcePortalSetting,
  location,
  defaultOpen,
  id,
  scrollTo,
  refsById,
  use = 'default'
}) => {
  const hasScope = useGetHasScope();
  const isUngated = useGetIsUngated();
  const history = useHistory();
  const expandedSectionIds = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.getAll(EXPANDED);
  }, [location]);
  const sectionRef = useRef(null);

  /* ignores any props and just wraps children unstyled */
  const BlankWrapper = useCallback(({
    children
  }) => /*#__PURE__*/_jsx(Fragment, {
    children: children
  }), []);
  const OuterWrapperComponent = use === 'unstyled' ? BlankWrapper : Tile;
  const InnerWrapperComponent = use === 'unstyled' ? BlankWrapper : TileSection;
  const tiles = /*#__PURE__*/_jsx(OuterWrapperComponent, {
    compact: true,
    children: modules.filter(({
      getIsVisible
    }) => !getIsVisible || getIsVisible({
      isUngated,
      hasScope,
      portalSettings: salesforcePortalSetting,
      settings
    })).map(({
      id: moduleId,
      Component
    }, index) => {
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(InnerWrapperComponent, {
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            renderError: () => /*#__PURE__*/_jsx(UIErrorMessage, {
              type: "badRequest",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "settingsV2.generalErrorMessage"
              })
            }),
            children: /*#__PURE__*/_jsx("div", {
              id: moduleId,
              ref: ref => {
                return refsById.current = refsById.current.set(moduleId, ref);
              },
              children: /*#__PURE__*/_jsx(Component, {
                flow: flow,
                settings: settings,
                readOnly: readOnly,
                onChange: onChange,
                scrollTo: scrollTo
              })
            })
          })
        }), index !== modules.length - 1 && /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        })]
      }, moduleId);
    })
  });
  if (use === 'unstyled') {
    return tiles;
  }
  if (use === 'default') {
    return /*#__PURE__*/_jsx(UIPanelSection, {
      children: /*#__PURE__*/_jsxs("div", {
        ref: ref => {
          sectionRef.current = ref;
          if (expandedSectionIds.includes(id) && sectionRef.current) {
            sectionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            atomicQueryUpdate({}, [EXPANDED], history);
          }
        },
        children: [/*#__PURE__*/_jsx(H5, {
          children: title
        }), tiles]
      })
    });
  }
  return /*#__PURE__*/_jsx(ExpandableSection, {
    indentation: "compact",
    defaultOpen: defaultOpen && expandedSectionIds.length === 0 || expandedSectionIds.includes(id),
    titleText: title,
    children: /*#__PURE__*/_jsx(View, {
      paddingInline: "300",
      children: tiles
    })
  });
};
export default SettingsSection;